import type { BrandingPublic } from '@accounts/shared';
import type { CSSProperties } from 'react';
import { Link } from 'react-router-dom';

import { STR } from '../i18n';
import { brandGlyph } from '../lib/branding';

/**
 * R2 — every brand mark in the app renders from `GET /api/branding`. When a logo is
 * configured the bytes come from `/api/branding/logo`; when one is not, the brand letter
 * stands in. No surface spells the product's name or draws its own mark.
 *
 * C4b — THE URL IS USED VERBATIM. `logoUrl` arrives as `/api/branding/logo?v=<token>`, the
 * token derived from the branding updated-at. Stripping or rebuilding it would restore the
 * constant URL that made a replaced logo keep serving from cache until a manual reload, so
 * nothing here touches it. The design of record paints it as a CSS `background-image` rather
 * than an `<img>`, which also keeps `contain` sizing on a fixed square.
 */

function logoLayer(logoUrl: string): CSSProperties {
  return {
    width: '100%',
    height: '100%',
    display: 'block',
    backgroundColor: 'var(--surface-2)',
    backgroundSize: 'contain',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    backgroundImage: `url("${logoUrl}")`,
  };
}

/**
 * The rail mark: an accent square carrying the logo, or the brand letter — and, per the
 * design of record, a CONTROL rather than decoration. It goes to the panel's first screen.
 */
export function RailBrandMark({
  branding,
  to,
}: {
  branding: BrandingPublic | undefined;
  to: string;
}) {
  const glyph = branding === undefined ? '' : brandGlyph(branding).charAt(0);
  const logoUrl = branding?.logoUrl ?? null;

  return (
    <Link
      to={to}
      aria-label={STR.shell.home}
      title={STR.shell.home}
      style={{
        width: 32,
        height: 32,
        borderRadius: 9,
        background: 'var(--accent)',
        color: 'var(--accent-fg)',
        display: 'grid',
        placeItems: 'center',
        fontSize: 14,
        fontWeight: 700,
        flex: 'none',
        overflow: 'hidden',
        padding: 0,
        cursor: 'pointer',
        textDecoration: 'none',
      }}
    >
      {logoUrl === null ? <span>{glyph}</span> : <span style={logoLayer(logoUrl)} />}
    </Link>
  );
}

/**
 * The sign-in panel's mark: the striped "no asset yet" slot with the logo, or the brand
 * letter, laid over it.
 */
export function SlotBrandMark({ logoUrl, glyph }: { logoUrl: string | null; glyph: string }) {
  return (
    <span
      className="logo-slot"
      style={{
        overflow: 'hidden',
        display: 'grid',
        placeItems: 'center',
        // The striped fill means "no asset yet". When the brand letter stands in it is a
        // deliberate mark, not a pending upload, so the stripes give way to a flat surface.
        ...(logoUrl === null ? { background: 'var(--surface-2)' } : {}),
      }}
      aria-hidden="true"
    >
      {logoUrl === null ? (
        <span style={{ fontSize: 14, fontWeight: 700 }}>{glyph.charAt(0)}</span>
      ) : (
        <span style={logoLayer(logoUrl)} />
      )}
    </span>
  );
}
